import React, { useState, useCallback, useEffect } from "react";
import { Panel, PanelHeader, PanelBody } from "../../components/panel/panel.jsx";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery.jsx";
import moment from "moment";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from '../../hooks/useThemeColor';

const CompanyDevices = ({ CompID }) => {
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  
  const [loading, setLoading] = useState(false);
  const [deviceList, setDeviceList] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [deviceData, setDeviceData] = useState({
    DeviceID: "",
    DeviceName: "",
    DeviceType: "",
    DeviceModel: "",
    SerialNumber: "",
    MACAddress: "",
    IPAddress: "",
    Location: "",
    IsActive: true,
    LastSeen: "",
    CompID: parseInt(CompID),
    AssignedUser: "",
    Notes: "",
  });

  const deviceTypes = [
    { value: "iPad", label: "iPad" },
    { value: "Android Tablet", label: "Android Tablet" },
    { value: "POS Terminal", label: "POS Terminal" },
    { value: "Kitchen Display", label: "Kitchen Display" },
    { value: "Customer Display", label: "Customer Display" },
    { value: "Mobile Phone", label: "Mobile Phone" },
    { value: "Other", label: "Other" },
  ];

  // Fetch company devices
  const fetchCompanyDevices = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_COMPANY_DEVICES, {
        params: { CompID: CompID },
      });

      if (response.data && response.data.data) {
        setDeviceList(response.data.data);
      }
    } catch (error) {
      showErrorToast("Failed to load company devices");
      console.error("Error fetching devices:", error);
    } finally {
      setLoading(false);
    }
  }, [CompID]);

  const handleInputChange = useCallback((e, field) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setDeviceData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleAddDevice = async () => {
    const { DeviceName, DeviceType, DeviceModel, SerialNumber, MACAddress, IPAddress, Location } = deviceData;

    if (!DeviceName.trim()) {
      showErrorToast("Please provide a device name");
      return;
    }

    if (!DeviceType.trim()) {
      showErrorToast("Please select a device type");
      return;
    }

    if (!DeviceModel.trim()) {
      showErrorToast("Please provide a device model");
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.post(APIs.ADD_COMPANY_DEVICE, deviceData);

      if (response.data.status) {
        showSuccessToast("Device added successfully");
        setShowAddForm(false);
        resetForm();
        fetchCompanyDevices();
      } else {
        showErrorToast(response.data.message || "Error adding device");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Error adding device");
      console.error("Error adding device:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateDevice = async () => {
    const { DeviceName, DeviceType, DeviceModel, SerialNumber, MACAddress, IPAddress, Location } = deviceData;

    if (!DeviceName.trim()) {
      showErrorToast("Please provide a device name");
      return;
    }

    if (!DeviceType.trim()) {
      showErrorToast("Please select a device type");
      return;
    }

    if (!DeviceModel.trim()) {
      showErrorToast("Please provide a device model");
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.put(APIs.UPDATE_COMPANY_DEVICE, deviceData);

      if (response.data.status) {
        showSuccessToast("Device updated successfully");
        setEditingDevice(null);
        resetForm();
        fetchCompanyDevices();
      } else {
        showErrorToast(response.data.message || "Error updating device");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Error updating device");
      console.error("Error updating device:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDevice = async (deviceID) => {
    if (!window.confirm("Are you sure you want to delete this device?")) {
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.delete(APIs.DELETE_COMPANY_DEVICE, {
        params: { DeviceID: deviceID, CompID: CompID },
      });

      if (response.data.status) {
        showSuccessToast("Device deleted successfully");
        fetchCompanyDevices();
      } else {
        showErrorToast(response.data.message || "Error deleting device");
      }
    } catch (err) {
      const message =
        err.response?.data?.message ||
        (typeof err.response?.data === "string" ? err.response.data : null) ||
        err.response?.statusText ||
        "Error deleting device";
      showErrorToast(message);
      console.error("Error deleting device:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = async (device) => {
    const deviceId = device.deviceID || device.DeviceID || "";
    if (!deviceId) {
      showErrorToast("Invalid device ID");
      return;
    }

    const isCurrentlyActive =
      String(device.deviceStatus || device.DeviceStatus || "").toLowerCase() === "1" ||
      String(device.deviceStatus || device.DeviceStatus || "").toLowerCase() === "active";

    try {
      setLoading(true);
      const response = await axiosInstance.patch(APIs.PATCH_UPDATE_DEVICE_STATUS, {
        CompID: parseInt(CompID),
        DeviceID: deviceId,
        Status: !isCurrentlyActive,
      });

      if (response.data.status) {
        showSuccessToast(`Device ${!isCurrentlyActive ? "activated" : "deactivated"} successfully`);
        fetchCompanyDevices();
      } else {
        showErrorToast(response.data.message || "Failed to update device status");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to update device status");
    } finally {
      setLoading(false);
    }
  };

  const handleEditDevice = (device) => {
    setEditingDevice(device);
    setDeviceData({
      DeviceID: device.deviceID || device.DeviceID || "",
      DeviceName: device.deviceName || device.DeviceName || device.name || device.Name || "",
      DeviceType: device.deviceType || device.DeviceType || device.app || device.App || device.os || device.OS || "",
      DeviceModel: device.deviceModel || device.DeviceModel || device.model || device.Model || "",
      SerialNumber: device.serialNumber || device.SerialNumber || device.deviceID || device.DeviceID || "",
      MACAddress: device.macAddress || device.MACAddress || "",
      IPAddress: device.ipAddress || device.IPAddress || device.device_Code || device.Device_Code || "",
      Location: device.location || device.Location || device.name || device.Name || "",
      IsActive: (device.deviceStatus || device.DeviceStatus) === "1" || device.isActive === true,
      LastSeen: device.lastSeen || device.registrationDate || device.RegistrationDate || "",
      CompID: parseInt(CompID),
      AssignedUser: device.assignedUser || device.AssignedUser || "",
      Notes: device.notes || device.Notes || "",
    });
    setShowAddForm(true);
  };

  const resetForm = () => {
    setDeviceData({
      DeviceID: "",
      DeviceName: "",
      DeviceType: "",
      DeviceModel: "",
      SerialNumber: "",
      MACAddress: "",
      IPAddress: "",
      Location: "",
      IsActive: true,
      LastSeen: "",
      CompID: parseInt(CompID),
      AssignedUser: "",
      Notes: "",
    });
    setEditingDevice(null);
    setShowAddForm(false);
  };

  const getDeviceStatus = (lastSeen) => {
    if (!lastSeen) return { status: "Unknown", class: "bg-secondary" };

    const lastSeenDate = moment(lastSeen);
    const now = moment();
    const diffMinutes = now.diff(lastSeenDate, 'minutes');

    if (diffMinutes < 5) return { status: "Online", class: "bg-success" };
    if (diffMinutes < 60) return { status: "Recent", class: "bg-warning" };
    return { status: "Offline", class: "bg-danger" };
  };

  const resolveDeviceValue = (device, keys, fallback = "") => {
    for (const key of keys) {
      const value = device?.[key];
      if (value !== null && value !== undefined && String(value).trim() !== "") {
        return value;
      }
    }
    return fallback;
  };

  const getDeviceStatusBySource = (device) => {
    const statusValue = resolveDeviceValue(device, ["deviceStatus", "DeviceStatus"], "");
    if (String(statusValue) === "1" || String(statusValue).toLowerCase() === "active") {
      return { status: "Active", class: "bg-success" };
    }
    if (String(statusValue) === "0" || String(statusValue).toLowerCase() === "inactive") {
      return { status: "Inactive", class: "bg-danger" };
    }

    const lastSeen = resolveDeviceValue(device, ["lastSeen", "registrationDate", "RegistrationDate"], null);
    return getDeviceStatus(lastSeen);
  };

  useEffect(() => {
    fetchCompanyDevices();
  }, [fetchCompanyDevices]);

  return (
    <div>
      {loading && <Preloader />}

      {/* Device List */}
      <Panel>
        <PanelHeader>Company Devices</PanelHeader>
        <PanelBody>
          {deviceList.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted">No devices found for this company.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped">
                <thead
                  style={{
                    backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                    color: isDarkMode ? "#ffffff" : "#000000",
                  }}
                >
                  <tr>
                    <th style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Device Name</th>
                    <th style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Type</th>
                    <th style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Model</th>
                    <th style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Serial Number</th>
                    <th style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>IP Address</th>
                    <th style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Location</th>
                    <th style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Status</th>
                    <th style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Last Seen</th>
                    <th style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {deviceList.map((device, index) => {
                    const deviceStatus = getDeviceStatusBySource(device);
                    const deviceName = resolveDeviceValue(device, ["deviceName", "DeviceName", "name", "Name"], "-");
                    const deviceType = resolveDeviceValue(device, ["app", "App", "os", "OS", "deviceType", "DeviceType"], "-");
                    const model = resolveDeviceValue(device, ["model", "Model", "deviceModel", "DeviceModel"], "-");
                    const serialNumber = resolveDeviceValue(device, ["deviceID", "DeviceID", "serialNumber", "SerialNumber"], "-");
                    const ipAddress = resolveDeviceValue(device, ["ipAddress", "IPAddress", "device_Code", "Device_Code"], "-");
                    const location = resolveDeviceValue(device, ["location", "Location", "name", "Name"], "-");
                    const lastSeen = resolveDeviceValue(device, ["lastSeen", "registrationDate", "RegistrationDate"], null);
                    const deviceId = resolveDeviceValue(device, ["deviceID", "DeviceID"], "");
                    return (
                      <tr key={index}>
                        <td>{deviceName}</td>
                        <td>{deviceType}</td>
                        <td>{model}</td>
                        <td>{serialNumber}</td>
                        <td>{ipAddress}</td>
                        <td>{location}</td>
                        <td>
                          <ToggleSwitch
                            isChecked={deviceStatus.status === "Active"}
                            onToggle={() => handleStatusToggle(device)}
                            label=""
                            name={`device-status-${deviceId}`}
                          />
                        </td>
                        <td>
                          {lastSeen ? moment(lastSeen).format('MMM DD, YYYY HH:mm') : 'Never'}
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary me-2"
                            style={{
                              width: '32px',
                              height: '32px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '6px',
                              border: `1px solid ${themeColor}`,
                              color: themeColor,
                              transition: 'all 0.2s ease',
                              padding: 0
                            }}
                            onClick={() => handleEditDevice(device)}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = themeColor;
                              e.currentTarget.style.color = 'white';
                              e.currentTarget.style.transform = 'scale(1.05)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.color = themeColor;
                              e.currentTarget.style.transform = 'scale(1)';
                            }}
                          >
                            <i className="fa fa-edit" style={{ fontSize: '12px' }}></i>
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            style={{
                              width: '32px',
                              height: '32px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '6px',
                              border: '1px solid #dc3545',
                              color: '#dc3545',
                              transition: 'all 0.2s ease',
                              padding: 0
                            }}
                            onClick={() => handleDeleteDevice(deviceId)}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#dc3545';
                              e.currentTarget.style.color = 'white';
                              e.currentTarget.style.transform = 'scale(1.05)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.color = '#dc3545';
                              e.currentTarget.style.transform = 'scale(1)';
                            }}
                          >
                            <i className="fa fa-trash" style={{ fontSize: '12px' }}></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </PanelBody>
      </Panel>

      {/* Add/Edit Device Form Modal */}
      {showAddForm && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingDevice ? "Edit Device" : "Add New Device"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={resetForm}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-6">
                    <FormInput
                      label="Device Name"
                      placeHolder="Enter Device Name"
                      type="text"
                      value={deviceData.DeviceName}
                      onchange={(e) => handleInputChange(e, "DeviceName")}
                    />
                  </div>
                  <div className="col-6">
                    <label>Device Type</label>
                    <select
                      className="form-control"
                      value={deviceData.DeviceType}
                      onChange={(e) => handleInputChange(e, "DeviceType")}
                    >
                      <option value="">Select Device Type</option>
                      {deviceTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-6">
                    <FormInput
                      label="Device Model"
                      placeHolder="Enter Device Model"
                      type="text"
                      value={deviceData.DeviceModel}
                      onchange={(e) => handleInputChange(e, "DeviceModel")}
                    />
                  </div>
                  <div className="col-6">
                    <FormInput
                      label="Serial Number"
                      placeHolder="Enter Serial Number"
                      type="text"
                      value={deviceData.SerialNumber}
                      onchange={(e) => handleInputChange(e, "SerialNumber")}
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-6">
                    <FormInput
                      label="MAC Address"
                      placeHolder="Enter MAC Address"
                      type="text"
                      value={deviceData.MACAddress}
                      onchange={(e) => handleInputChange(e, "MACAddress")}
                    />
                  </div>
                  <div className="col-6">
                    <FormInput
                      label="IP Address"
                      placeHolder="Enter IP Address"
                      type="text"
                      value={deviceData.IPAddress}
                      onchange={(e) => handleInputChange(e, "IPAddress")}
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-6">
                    <FormInput
                      label="Location"
                      placeHolder="Enter Device Location"
                      type="text"
                      value={deviceData.Location}
                      onchange={(e) => handleInputChange(e, "Location")}
                    />
                  </div>
                  <div className="col-6">
                    <FormInput
                      label="Assigned User"
                      placeHolder="Enter Assigned User"
                      type="text"
                      value={deviceData.AssignedUser}
                      onchange={(e) => handleInputChange(e, "AssignedUser")}
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-6">
                    <ToggleSwitch
                      isChecked={deviceData.IsActive}
                      onToggle={(e) => handleInputChange(e, "IsActive")}
                      label="Active Status"
                      name="IsActive"
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-12">
                    <label>Notes</label>
                    <textarea
                      className="form-control"
                      placeholder="Enter device notes"
                      value={deviceData.Notes}
                      onChange={(e) => handleInputChange(e, "Notes")}
                      rows="3"
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={resetForm}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={editingDevice ? handleUpdateDevice : handleAddDevice}
                  disabled={loading}
                >
                  {loading ? "Processing..." : editingDevice ? "Update" : "Add"} Device
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyDevices;
